import { useState, useRef, useEffect, HTMLProps } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import * as S from "./styles";
import { useTranslation } from "react-i18next";

interface SearchableSelectInputProps<T extends Record<string, any>>
  extends HTMLProps<HTMLInputElement> {
  initialValue?: T;
  options: T[];
  onOptionSelect: (option: T) => void;
  title?: string;
  disabled?: boolean;
}

export function SearchableSelectInput<T extends Record<string, any>>({
  disabled,
  initialValue,
  options,
  onOptionSelect,
  title,
  ...rest
}: SearchableSelectInputProps<T>) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(initialValue?.name || "");
  const [selectedOption, setSelectedOption] = useState(initialValue);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const placeholder = rest.placeholder || `${t("search")}...`;

  const filteredOptions = options.filter((option) => {
    if (!option.name) return false;
    return option.name.toLowerCase().includes(inputValue.toLowerCase());
  });

  const handleOptionClick = (option: T) => {
    setInputValue(option.name);
    setSelectedOption(option);
    setIsOpen(false);
    onOptionSelect(option);
  };

  const handleInputFocus = () => {
    if (!isOpen) {
      setIsOpen(true);
      if (selectedOption) {
        setInputValue("");
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsOpen(true);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setInputValue(selectedOption?.name || "");
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedOption]);

  return (
    <>
      {title && <S.Title>{title}</S.Title>}
      <S.DropdownContainer
        ref={dropdownRef}
        disabled={disabled}
        title={selectedOption?.name}
      >
        <S.InputWrapper>
          <input
            type="text"
            value={inputValue}
            placeholder={placeholder}
            ref={inputRef}
            onFocus={handleInputFocus}
            onChange={handleInputChange}
            disabled={disabled}
            {...rest}
          />
          <MdKeyboardArrowDown
            className="arrow-icon"
            onClick={(e) => {
              e.preventDefault();
              if (disabled) return;

              if (!isOpen) {
                handleInputFocus();
              } else {
                setIsOpen(false);
              }
            }}
          />
        </S.InputWrapper>

        {isOpen && filteredOptions.length > 0 && (
          <S.OptionsList>
            {filteredOptions.map((option) => (
              <S.OptionItem
                key={option.id}
                onClick={() => handleOptionClick(option)}
              >
                {option.name}
              </S.OptionItem>
            ))}
          </S.OptionsList>
        )}
        {isOpen && filteredOptions.length === 0 && (
          <S.NoOptionsMessage>{t("noResultsFound")}</S.NoOptionsMessage>
        )}
      </S.DropdownContainer>
    </>
  );
}
